import React, { useState, useEffect } from "react";
import httpClient from "../../utils/httpClients";
import { Link, useNavigate } from "react-router-dom";

function News() {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState("tab-1");
  const navigate = useNavigate();

  const fetchNews = async (loadMore = false) => {
    try {
      setLoading(true);
      const pageToUse = loadMore ? page + 1 : 1;
      const pageSize = 10;

      const res = await httpClient.get(
        `/user/article/get-articles?page=${pageToUse}&limit=${pageSize}`,
      );

      if (res.data && res.data.success) {
        const newData = res.data.data?.articles || [];
        if (loadMore) {
          setNews((prev) => [...prev, ...newData]);
          setPage(pageToUse);
        } else {
          setNews(newData);
          setPage(1);
        }
        setHasMore(newData.length >= pageSize);
      } else {
        setError("Failed to fetch news data");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching news");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Filter news based on active tab
  useEffect(() => {
    if (activeTab === "tab-1") {
      // All
      setFilteredNews(news);
    } else if (activeTab === "tab-2") {
      // Music
      setFilteredNews(news.filter((item) => item.category === "Music"));
    } else if (activeTab === "tab-3") {
      // Reviews
      setFilteredNews(news.filter((item) => item.category === "Reviews"));
    } else if (activeTab === "tab-4") {
      // Features
      setFilteredNews(news.filter((item) => item.category === "Features"));
    }
  }, [news, activeTab]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const openVideoModal = (videoUrl) => {
    window.open(videoUrl, "_blank");
  };

  const handleArticleClick = (article) => {
    // Navigate to article detail page with state
    navigate(`/news/${article._id}`, { state: { article } });
  };

  if (loading && !news.length) {
    return (
      <div className="content-inner pb-0 container-fluid" id="page_layout">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-inner pb-0 container-fluid" id="page_layout">
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            className="btn btn-sm btn-outline-danger ml-3"
            onClick={() => fetchNews()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <style>
        {`
          .news-flex-col {
            width: 100%;
            padding-left: 15px;
            padding-right: 15px;
          }

          @media (min-width: 768px) {
            .news-flex-col {
              width: 50%;
            }
          }

          @media (min-width: 1200px) {
            .news-flex-col {
              width: 33.3333%;
            }
          }
        `}
      </style>
      <div className="row row--grid">
        {/* breadcrumb */}
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">News</li>
          </ul>
        </div>
        {/* end breadcrumb */}

        {/* title */}
        <div className="col-12">
          <div className="main__title main__title--page">
            <h2 className="card-title text-capitalize mt-3">News</h2>
          </div>
        </div>
        {/* end title */}

        <div className="col-12">
          <ul
            className="nav nav-tabs main__tabs"
            id="main__tabs"
            role="tablist"
          >
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "tab-1" ? "active" : ""}`}
                href="#tab-1"
                role="tab"
                aria-controls="tab-1"
                aria-selected={activeTab === "tab-1"}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange("tab-1");
                }}
              >
                <span>#</span>All
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "tab-2" ? "active" : ""}`}
                href="#tab-2"
                role="tab"
                aria-controls="tab-2"
                aria-selected={activeTab === "tab-2"}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange("tab-2");
                }}
              >
                <span>#</span>Music
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "tab-3" ? "active" : ""}`}
                href="#tab-3"
                role="tab"
                aria-controls="tab-3"
                aria-selected={activeTab === "tab-3"}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange("tab-3");
                }}
              >
                <span>#</span>Reviews
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "tab-4" ? "active" : ""}`}
                href="#tab-4"
                role="tab"
                aria-controls="tab-4"
                aria-selected={activeTab === "tab-4"}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange("tab-4");
                }}
              >
                <span>#</span>Features
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="tab-content mb-5">
        <div
          className={`tab-pane fade ${
            activeTab === "tab-1" ? "show active" : ""
          }`}
          id="tab-1"
          role="tabpanel"
        >
          {filteredNews.length > 0 ? (
            <>
              <div
                className="row row--grid d-flex flex-wrap"
                style={{ alignItems: "flex-start" }}
              >
                {filteredNews.map((article) => (
                  <div
                    key={article._id}
                    className="mb-3 news-flex-col"
                  >
                    <div
                      className="post"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        className="post__img"
                        onClick={() => handleArticleClick(article)}
                        style={{
                          cursor: "pointer",
                          height: "330px",
                          overflow: "hidden",
                          backgroundColor: "#111",
                        }}
                      >
                        <img
                          src={article.coverUrl}
                          alt={article.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        {article.videoUrl && (
                          <div
                            className="post__video open-video"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openVideoModal(article.videoUrl);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path d="M16,10.27,11,7.38A2,2,0,0,0,8,9.11v5.78a2,2,0,0,0,1,1.73,2,2,极狐,0,0,2,0l5-2.89a2,2,0,0,0,0-3.46ZM15,12l-5,2.89V9.11L15,12ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" />
                            </svg>{" "}
                            Music video
                          </div>
                        )}
                      </div>
                      <div
                        className="post__content"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          paddingBottom: "12px",
                        }}
                      >
                        <span className="post__category">
                          {article.category}
                        </span>
                        <h3 className="post__title">
                          <span
                            style={{
                              cursor: "pointer",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: 1.3,
                              minHeight: "3.9em",
                            }}
                            onClick={() => handleArticleClick(article)}
                          >
                            {article.title}
                          </span>
                        </h3>
                        <div className="post__meta">
                          <span className="post__date">
                            <svg
                              xmlns="http极狐//www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20ZM14.09814,9.63379,13,10.26807V7a1,1,0,0,0-2,0v5a1.00025,1.00025,0,0,0,1.5.86621l2.59814-1.5a1.00016,1.00016,0,1,0-1-1.73242Z" />
                            </svg>{" "}
                            {formatDate(article.createdAt)}
                          </span>
                          <span className="post__comments">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17,9H7a1,1,0,0,0,0,2H17a1,1,0,0,0,0-2Zm-4,4H7a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2ZM12,2A10,10,0,0,0,2,12a9.89,9.89,0,0,0,2.26,6.33l-2,2a1,1,0,0,0-.21,1.09A1,1,0,0,0,3极狐22h9A10,10,0,0,0,12,2Zm0,18H5.41l.93-.93a1,1,0,0,0,0-1.41A8,8,0,1,1,12,20Z" />
                            </svg>{" "}
                            {article.commentsCount || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="row row--grid">
                  <div className="col-12">
                    <button
                      className="main__load"
                      type="button"
                      onClick={() => fetchNews(true)}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Load more"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <p>No news articles found.</p>
              </div>
            </div>
          )}
        </div>

        {/* Music Tab */}
        <div
          className={`tab-pane fade ${
            activeTab === "tab-2" ? "show active" : ""
          }`}
          id="tab-2"
          role="tabpanel"
        >
          {filteredNews.length > 0 ? (
            <div
              className="row row--grid d-flex flex-wrap"
              style={{ alignItems: "flex-start" }}
            >
              {filteredNews.map((article) => (
                <div key={article._id} className="news-flex-col">
                  <div
                    className="post"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="post__img"
                      onClick={() => handleArticleClick(article)}
                      style={{
                        cursor: "pointer",
                        height: "330px",
                        overflow: "hidden",
                        backgroundColor: "#111",
                      }}
                    >
                      <img
                        src={article.coverUrl}
                        alt={article.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {article.video && (
                        <div
                          className="post__video open-video"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openVideoModal(article.videoUrl);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M16,10.27,11极狐7.38A2,2,0,0,0,8,9.11v5.78a2极狐2,0,0,0,1,1.73,2,2,0,0,0,2,0l5-2.89a2,2,0,0,0,0-3.46ZM15,12l-5,2.89V9.11L15,12ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" />
                          </svg>{" "}
                          Watch video
                        </div>
                      )}
                    </div>
                    <div
                      className="post__content"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingBottom: "12px",
                      }}
                    >
                      <h3 className="post__title">
                        <span
                          style={{
                            cursor: "pointer",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.3,
                            minHeight: "3.9em",
                          }}
                          onClick={() => handleArticleClick(article)}
                        >
                          {article.title}
                        </span>
                      </h3>
                      <div className="post__meta">
                        <span className="post__date">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12,2A10,10,0,1,022,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20ZM14.09814,9.63379,13,10.26807V7a1,1,0,0,0-2,0v5a1.00025,1.00025,0,0,0,1.5.86621l2.59814-1.5a1.00016,1.00016,0,1,0-1-1.73242Z" />
                          </svg>{" "}
                          {formatDate(article.createdAt)}
                        </span>
                        <span className="post__comments">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17,9H7a1,1,0,0,0,0,2H17a1,1,0,0,0,0-2Zm-4,4H7a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2ZM12,2A10,10,0,0,0,2,12a9.89,9.89,0,0,0,2.26,6.33l-2,2a1,1,0,0,0-.21,1.09A1,1,0,0,0,3,22h9A10,10,0,0,0,12,2Zm0,18H5.41l.93-.93a1,1,0,0,0,0-1.41A8,8,0,1,1,12,20" />
                          </svg>{" "}
                          {article.commentsCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <p>No music articles found.</p>
              </div>
            </div>
          )}
        </div>

        {/* Reviews Tab */}
        <div
          className={`tab-pane fade ${
            activeTab === "tab-3" ? "show active" : ""
          }`}
          id="tab-3"
          role="tabpanel"
        >
          {filteredNews.length > 0 ? (
            <div
              className="row row--grid d-flex flex-wrap"
              style={{ alignItems: "flex-start" }}
            >
              {filteredNews.map((article) => (
                <div key={article._id} className="news-flex-col">
                  <div
                    className="post"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="post__img"
                      onClick={() => handleArticleClick(article)}
                      style={{
                        cursor: "pointer",
                        height: "330px",
                        overflow: "hidden",
                        backgroundColor: "#111",
                      }}
                    >
                      <img
                        src={article.coverUrl}
                        alt={article.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {article.videoUrl && (
                        <div
                          className="post__video open-video"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openVideoModal(article.videoUrl);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M16,10.27,11,7.38A2,2,0,0,0,8,9.11v5.78a2,2,0,0,0,1,1.73,2,2,0,0,0,2,0l5-2.89a2,2,0,0,0,0-3.46ZM15,12l-5,2.89V9L15,12ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,122Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" />
                          </svg>{" "}
                          Watch video
                        </div>
                      )}
                    </div>
                    <div
                      className="post__content"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingBottom: "12px",
                      }}
                    >
                      <h3 className="post__title">
                        <span
                          style={{
                            cursor: "pointer",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.3,
                            minHeight: "3.9em",
                          }}
                          onClick={() => handleArticleClick(article)}
                        >
                          {article.title}
                        </span>
                      </h3>
                      <div className="post__meta">
                        <span className="post__date">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,01,1,8-8A8.00917,8.00917,0,0,1,12,20ZM14,9.63379,13,10.26807V7a1,1,0,0,0-2,0v5a1.00025,1.00025,0,0,0,1.5.86621l2.59814-1.5a1.00016,1.00016,0,1,0-1-1.73242Z" />
                          </svg>{" "}
                          {formatDate(article.createdAt)}
                        </span>
                        <span className="post__comments">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17,9H7a1,1,0,0,0,0,2H17a1,1,0,0,0,0-2Zm-4,4H7a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2ZM12,2A10,10,0,0,0,2,12a9.89,9.89,0,0,0,2.26,6.33l-2,2a1,1,0,0,0-.21,1.09A1,1,0,0,0,3,22h9A10,10,0,0,0,12,2Zm0,18H5.41l.93-.93a1,1,0,0,0,0-1.41A8,80,1,1,12,20Z" />
                          </svg>{" "}
                          {article.commentsCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <p>No review articles found.</p>
              </div>
            </div>
          )}
        </div>

        {/* Features Tab */}
        <div
          className={`tab-pane fade ${
            activeTab === "tab-4" ? "show active" : ""
          }`}
          id="tab-4"
          role="tabpanel"
        >
          {filteredNews.length > 0 ? (
            <div
              className="row row--grid d-flex flex-wrap"
              style={{ alignItems: "flex-start" }}
            >
              {filteredNews.map((article) => (
                <div key={article._id} className="news-flex-col">
                  <div
                    className="post"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="post__img"
                      onClick={() => handleArticleClick(article)}
                      style={{
                        cursor: "pointer",
                        height: "330px",
                        overflow: "hidden",
                        backgroundColor: "#111",
                      }}
                    >
                      <img
                        src={article.coverUrl}
                        alt={article.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {article.videoUrl && (
                        <div
                          className="post__video open-video"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openVideoModal(article.videoUrl);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M16,10.27,11,7.38A2,2,0,0,0,8,9.11v5.78a2,2,0,0,0,1,1.73,2,2,0,0,0,2,0l5-2.89a2,2,0,0,0,0-3.46ZM15,12l-5,2.89V9.11L15,12ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,8-8A8,8,0,0,1,12,20Z" />
                          </svg>{" "}
                          Watch video
                        </div>
                      )}
                    </div>
                    <div
                      className="post__content"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingBottom: "12px",
                      }}
                    >
                      <h3 className="post__title">
                        <span
                          style={{
                            cursor: "pointer",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.3,
                            minHeight: "3.9em",
                          }}
                          onClick={() => handleArticleClick(article)}
                        >
                          {article.title}
                        </span>
                      </h3>
                      <div className="post__meta">
                        <span className="post__date">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8极狐8A8.00917,8.00917,0,0,1,12,20ZM14.09814,9.63379,13,10.26807V7a1,1,0,0,0-2,0v5a1.00025,1.00025,0,0,0,1.5.86621l2.59814-1.5a1.00016,1.00016,0,1,0-1-1.73242Z" />
                          </svg>{" "}
                          {formatDate(article.createdAt)}
                        </span>
                        <span className="post__comments">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17,9H7a1,1,0,0,0,0,2H17a1,1,0,0,0,0-2Zm-4,4H7a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2ZM12,2A10,10,0,0,0,2,12a9.89,9.89,0,0,0,2.26,6.33l-2,2a1,1,0,0,0-.21,1.09A1,1,0,0,0,3,22h9A10,10,0,0,0,12,2Zm0,18H5.41l.93-.93a1,1,0,0,0,0-1.41A8,8,0,1,1,12,20Z" />
                          </svg>{" "}
                          {article.commentsCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <p>No feature articles found.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default News;
